import { useMemo, useState } from "react";
import { Box, Button, IconButton, LinearProgress, MenuItem, Select, Slider, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DrawIcon from "@mui/icons-material/Draw";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import { useDataReview } from "../../contexts/DataReviewContext";
import { DEFAULT_THRESHOLD, passThreshold } from "../dataReview/visibility";
import { FAINT, LINE, MONO, MUTED, TEAL, TEXT, btnAccept, btnGhost, btnPlain, btnReject, btnSmall, capSx, floatSx, inputSx, menuItemSx, pillSx, swatchSx } from "./editorTheme";
import { fmtArea, geometryPath, PAINT_TERM, targetLabel, termOf, thumbCrop } from "./editorModel";
import { useChangesByIds } from "../../api/dataReview";
import { useEditorData, workerStarting } from "./useEditorData";
import { useEditorActions } from "./useEditorActions";
import { useVegEditor } from "./VegEditorContext";

const THUMB_W = 92, THUMB_H = 62, FILM = 7;

/** The dock along the bottom of a refinement: the active target, what its
 *  findings become, the confidence cut-off, how far the
 *  review is, bulk accept / reject, and a filmstrip of what is left, least
 *  sure first. A lasso selection replaces the filmstrip with its own bar. */
export default function VegEditorDock() {
  const d = useEditorData();
  const a = useEditorActions();
  const { activeTerm, queued } = useVegEditor();
  const { currentId, selectedIds, setSelectedIds } = useDataReview();
  const g = activeTerm ? d.groups[activeTerm] : undefined;
  const job = d.job;
  const colour = g ? (d.colours[g.target] ?? (g.key === PAINT_TERM ? TEAL : "#b35bd1")) : TEAL;
  const saved = g && job ? (g.pass !== undefined ? passThreshold(job, g.pass, a.passThresholds) : 0) : DEFAULT_THRESHOLD;
  // The slider moves on its own while dragged; the cut-off (and the three
  // requests that follow it) changes once, when it is let go.
  const [dragging, setDragging] = useState<{ key: string; value: number } | null>(null);
  const thr = dragging && g && dragging.key === g.key ? dragging.value : saved;
  const bins = g?.histogram ?? [];
  const maxBin = Math.max(1, ...bins);
  // The target's review order comes from the server as ids; only the few
  // the filmstrip shows are fetched in full.
  const order = a.order;
  const filmIds = useMemo(() => {
    const pending = order.filter((o) => o.status === "pending");
    const idx = Math.max(0, pending.findIndex((o) => o.id === currentId));
    const start = Math.max(0, Math.min(idx - 1, pending.length - FILM));
    return pending.slice(start, start + FILM).map((o) => o.id);
  }, [order, currentId]);
  const { data: filmRes } = useChangesByIds(d.projectId, job?.id, filmIds);
  const film = useMemo(() => {
    const byId = new Map((filmRes?.data?.features ?? []).map((f) => [f.properties.id, f]));
    return filmIds.map((id) => byId.get(id)).filter((f): f is NonNullable<typeof f> => !!f);
  }, [filmRes, filmIds]);
  const decided = g ? g.accepted + g.rejected : 0;
  // The shape in hand, when it belongs to this target and no lasso
  // selection is open: its own line, with accept / reject / edit.
  const cur = g && !selectedIds.length && d.current && termOf(d.current) === g.key ? d.current : null;
  const curPos = cur ? order.findIndex((o) => o.id === cur.properties.id) : -1;

  const wrap = (children: React.ReactNode, width = 1000) => (
    <Box sx={{ ...floatSx, position: "fixed", left: "50%", bottom: 12, transform: "translateX(-50%)", width, maxWidth: "calc(100% - 140px)", zIndex: 1100 }}>{children}</Box>
  );

  // The run failed: say why, in the run's own words, and offer the way out.
  if (job?.status === "failed") {
    return wrap(
      <Box sx={{ p: "14px 16px" }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#ffc2a6" }}>This refinement could not start</Typography>
        <Typography sx={{ fontSize: 12.5, color: TEXT, mt: 0.75, lineHeight: 1.5 }}>{job.error || "Something went wrong on the server."}</Typography>
        <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
          <Button sx={{ ...btnReject, ...btnSmall }} onClick={() => a.discardJob(job.id)}>Discard refinement</Button>
          <Button sx={{ ...btnPlain, ...btnSmall }} onClick={() => { a.discardJob(job.id); a.newRefinement(); }}>Start again with a smaller area</Button>
        </Box>
      </Box>, 520,
    );
  }
  // Nothing to review for this target yet: say what is happening instead.
  if (!g || !g.total) {
    const q = queued[0];
    const sent = queued.find((x) => x.sent);
    const text = workerStarting(job)
      ? "Starting up… This takes a few minutes when the tool hasn't been used for a while."
      : d.runningPass && job
      ? `${job.stage || "Looking"}…`
      : sent ? `Starting to look for ${sent.label.toLowerCase()}…`
      : d.preparing ? (q ? `${q.label} will be looked for once the image is ready.` : "Preparing the image.")
        : q ? `${q.label} is next.` : activeTerm ? "Nothing found. Try another word, or paint it by hand." : "Ask for something above, or paint by hand.";
    if (!activeTerm && !d.runningPass && !q && !d.preparing) return null;
    return wrap(
      <Box sx={{ p: "14px 16px" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={swatchSx(colour)} />
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{g?.label ?? q?.label ?? (activeTerm ? activeTerm : "Working")}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {d.runningPass && <Button sx={{ ...btnPlain, ...btnSmall }} onClick={a.stop}>Stop</Button>}
            {activeTerm && <IconButton size="small" onClick={() => a.removeTerm(activeTerm)} aria-label="Remove this target" sx={{ color: MUTED }}><CloseIcon sx={{ fontSize: 15 }} /></IconButton>}
          </Box>
        </Box>
        <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.75, lineHeight: 1.5 }}>{text}</Typography>
        {(d.runningPass || sent) && job && (
          <LinearProgress variant={d.runningPass ? "determinate" : "indeterminate"} value={job.progress} sx={{ mt: 1.25, height: 4, borderRadius: 2, bgcolor: "rgba(255,255,255,0.12)", "& .MuiLinearProgress-bar": { bgcolor: "#6aa8ff" } }} />
        )}
      </Box>, 520,
    );
  }

  const targetOptions = Array.from(new Set([g.target, "Lawn", "Tree", "Shrub", "Cleared", "Water", "Building", ...d.classes, "Unchanged"]));
  const onSlider = (v: number | number[]) => setDragging({ key: g.key, value: Array.isArray(v) ? v[0] : v });
  const onSliderDone = (v: number | number[]) => {
    const t = Array.isArray(v) ? v[0] : v;
    setDragging(null);
    if (g.pass !== undefined && t !== saved) a.moveThreshold(g.pass, t);
  };
  return wrap(
    <>
      <Box sx={{ display: "flex", gap: 2.75, p: "12px 14px", alignItems: "flex-start", flexWrap: "wrap" }}>
        <Box sx={{ width: 210, flex: "none" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={swatchSx(colour)} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.label}</Typography>
            <Typography sx={{ fontSize: 12, color: MUTED, flex: 1, whiteSpace: "nowrap" }}>{g.total.toLocaleString()} found</Typography>
            <IconButton size="small" onClick={() => a.removeTerm(g.key)} aria-label="Remove this target" title="Remove this target" sx={{ color: MUTED, p: 0.25 }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "60px minmax(0, 1fr)", gap: 0.75, alignItems: "center", mt: 1.25 }}>
            <Typography sx={{ fontSize: 12, color: MUTED }}>Becomes</Typography>
            <Select size="small" value={g.target} onChange={(ev) => a.retarget(g.key, { target: String(ev.target.value) })} sx={{ ...inputSx, height: 28, fontSize: 12 }} MenuProps={{ PaperProps: { sx: { bgcolor: "#1d2733", color: TEXT } } }}>
              {targetOptions.map((t) => <MenuItem key={t} value={t} sx={menuItemSx}>{targetLabel(t)}</MenuItem>)}
            </Select>
            <Typography sx={{ fontSize: 12, color: MUTED }}>Asset</Typography>
            <Select size="small" displayEmpty value={g.assetType} onChange={(ev) => a.retarget(g.key, { asset_type: String(ev.target.value) })} sx={{ ...inputSx, height: 28, fontSize: 12 }} MenuProps={{ PaperProps: { sx: { bgcolor: "#1d2733", color: TEXT } } }}>
              <MenuItem value="" sx={menuItemSx}>No asset</MenuItem>
              {(d.catalogue?.asset_types ?? []).map((t) => <MenuItem key={t.value} value={t.value} sx={menuItemSx}>{t.label}</MenuItem>)}
            </Select>
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: "2px", height: 34, px: 0.9 }}>
            {bins.map((n: number, i: number) => {
              const lo = (i / bins.length) * 100;
              const bg = lo + 100 / bins.length <= thr ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.75)";
              return <Box key={i} sx={{ flex: 1, minHeight: 2, height: `${Math.max(2, (n / maxBin) * 34)}px`, borderRadius: "2px 2px 0 0", bgcolor: bg }} />;
            })}
          </Box>
          <Slider size="small" min={0} max={100} step={1} value={thr} onChange={(_, v) => onSlider(v)} onChangeCommitted={(_, v) => onSliderDone(v)}
            sx={{ color: TEAL, py: 0.75, "& .MuiSlider-thumb": { width: 14, height: 14, bgcolor: "#f1f5f8", border: "2px solid #1b222b", boxShadow: `0 0 0 1px ${TEAL}` }, "& .MuiSlider-rail": { opacity: 0.35 } }}
            aria-label="Hide shapes below this confidence" />
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", fontSize: 12, mt: 0.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}><Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: "rgba(255,255,255,0.18)", border: `1px solid ${FAINT}` }} /><span>Hidden under {thr}% · {g.hidden}</span></Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}><Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: "rgba(255,255,255,0.75)" }} /><span>To review · {g.pending}</span></Box>
          </Box>
        </Box>
        <Box sx={{ width: 210, flex: "none", display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}><Typography sx={{ fontSize: 12, color: MUTED }}>Reviewed</Typography><Typography sx={{ fontFamily: MONO, fontSize: 12 }}>{decided} / {g.shown}</Typography></Box>
          <LinearProgress variant="determinate" value={g.shown ? (decided / g.shown) * 100 : 0} sx={{ height: 4, borderRadius: 2, bgcolor: "rgba(255,255,255,0.12)", "& .MuiLinearProgress-bar": { bgcolor: TEAL } }} />
          <Box sx={{ display: "flex", gap: 0.75, mt: 0.25 }}>
            <Button sx={{ ...btnAccept, ...btnSmall, flex: 1 }} disabled={!g.pending} onClick={() => a.bulkTerm(g.key, "accepted")}>Accept {g.pending}</Button>
            <Button sx={{ ...btnReject, ...btnSmall, flex: 1 }} disabled={!g.pending} onClick={() => a.bulkTerm(g.key, "rejected")}>Reject {g.pending}</Button>
          </Box>
        </Box>
      </Box>
      {cur && (() => {
        const p = cur.properties;
        const status = p.status === "pending" ? "To review" : p.status === "accepted" ? "Accepted" : p.status === "rejected" ? "Rejected" : "Applied";
        const tone = p.status === "accepted" ? "ok" : "plain";
        return (
          <Box sx={{ borderTop: `1px solid ${LINE}`, p: "8px 14px", display: "flex", gap: 1.25, alignItems: "center" }}>
            <Box sx={swatchSx(d.colours[p.target] ?? colour)} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
              {p.label === "Painted" ? `Painted · ${p.target}` : p.label}
            </Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 11.5, color: MUTED, whiteSpace: "nowrap" }}>{Math.round(p.confidence)}% · {fmtArea(p.area_m2)}</Typography>
            <Box sx={{ ...pillSx(tone), ...(p.status === "rejected" ? { bgcolor: "rgba(217,83,79,0.22)", color: "#ffc2a6" } : {}) }}>{status}</Box>
            <Box sx={{ display: "flex", alignItems: "center", ml: 0.5 }}>
              <IconButton size="small" onClick={() => a.step(-1)} aria-label="Previous shape" title="Previous shape (←)" sx={{ color: MUTED }}><ChevronLeftIcon sx={{ fontSize: 18 }} /></IconButton>
              <Typography sx={{ fontFamily: MONO, fontSize: 11.5, color: MUTED, whiteSpace: "nowrap" }}>{curPos >= 0 ? `${curPos + 1} of ${order.length}` : "hidden"}</Typography>
              <IconButton size="small" onClick={() => a.step(1)} aria-label="Next shape" title="Next shape (→)" sx={{ color: MUTED }}><ChevronRightIcon sx={{ fontSize: 18 }} /></IconButton>
            </Box>
            <Box sx={{ flex: 1 }} />
            <Button sx={{ ...btnPlain, ...btnSmall, gap: 0.5 }} onClick={() => a.zoomTo(p.id)} title="Zoom the map to this shape (Z)"><CenterFocusStrongIcon sx={{ fontSize: 15 }} />Zoom to</Button>
            <Button sx={{ ...btnPlain, ...btnSmall, gap: 0.5 }} onClick={a.beginEdit} title="Edit the outline (E)"><DrawIcon sx={{ fontSize: 15 }} />Edit outline</Button>
            <Button sx={{ ...btnReject, ...btnSmall, minWidth: 76 }} onClick={() => a.decide("rejected")} title="Reject (X)">Reject</Button>
            <Button sx={{ ...btnAccept, ...btnSmall, minWidth: 76 }} onClick={() => a.decide("accepted")} title="Accept (A)">Accept</Button>
          </Box>
        );
      })()}
      <Box sx={{ borderTop: `1px solid ${LINE}`, p: "8px 14px 10px", display: "flex", gap: 1, alignItems: "center", minHeight: 82, overflow: "hidden" }}>
        {selectedIds.length > 0 ? (
          <>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{selectedIds.length} shapes selected</Typography>
            <Typography sx={{ fontSize: 12, color: MUTED }}>Lasso again to replace the selection</Typography>
            <Box sx={{ flex: 1 }} />
            <Button sx={{ ...btnGhost, ...btnSmall }} onClick={() => setSelectedIds([])}>Clear</Button>
            <Button sx={btnReject} onClick={() => a.decide("rejected")}>Reject</Button>
            <Button sx={btnAccept} onClick={() => a.decide("accepted")}>Accept</Button>
          </>
        ) : film.length > 0 ? (
          <>
            <Typography sx={{ ...capSx, width: 52, flex: "none", lineHeight: 1.3 }}>Least sure first</Typography>
            {film.map((f) => {
              const crop = thumbCrop(job?.overlays ?? [], f, THUMB_W - 4, THUMB_H - 4);
              const on = f.properties.id === currentId;
              return (
                <Box key={f.properties.id} component="button" onClick={() => a.select(f.properties.id)} aria-label={`Shape at ${Math.round(f.properties.confidence)}% confidence`}
                  sx={{ position: "relative", flex: "none", width: THUMB_W, height: THUMB_H, borderRadius: 1.75, overflow: "hidden", p: 0, cursor: "pointer", bgcolor: "#0e1318",
                    border: `2px solid ${on ? TEAL : "rgba(255,255,255,0.15)"}`, "&:hover": { borderColor: on ? TEAL : "rgba(255,255,255,0.4)" } }}>
                  {crop && <img src={crop.url} alt="" draggable={false} style={{ position: "absolute", left: crop.left, top: crop.top, width: crop.width, height: crop.height, maxWidth: "none" }} />}
                  {crop && (
                    <svg viewBox={crop.viewBox} preserveAspectRatio="none" style={{ position: "absolute", left: 0, top: 0, width: THUMB_W - 4, height: THUMB_H - 4 }}>
                      <path d={geometryPath(f.geometry)} fill="none" stroke={TEAL} strokeWidth="1.5" vectorEffect="non-scaling-stroke" fillRule="evenodd" />
                    </svg>
                  )}
                  <Box component="span" sx={{ position: "absolute", right: 4, bottom: 4, fontFamily: MONO, fontSize: 10, px: 0.5, borderRadius: 0.75, bgcolor: "rgba(11,15,20,0.8)", color: TEXT }}>{Math.round(f.properties.confidence)}%</Box>
                </Box>
              );
            })}
            <Box sx={{ flex: 1 }} />
            <Button sx={btnPlain} onClick={a.goNext}>Next to review</Button>
          </>
        ) : (
          <Typography sx={{ fontSize: 12.5, color: MUTED }}>{g.shown ? "Everything above the cut-off is reviewed." : "Everything is under the cut-off. Lower it to see more."}</Typography>
        )}
      </Box>
    </>,
  );
}
